import React, { useState } from 'react';
import { Lock, ArrowRight, Crown, Rocket, Coins } from 'lucide-react';
import { stripeService, CREDIT_COSTS } from '../services/stripeService';
import { authManager } from '../managers/AuthManager';
import CreditPurchaseModal from './CreditPurchaseModal';
import './UpgradeModal.css';

const TIER_NAMES = { basic: 'Basic', pro: 'Pro', max: 'Max' };
const NEXT_TIER = { basic: 'pro', pro: 'max' };

const FEATURE_LABELS = {
    resume_builds: 'Resume Builds',
    score_checks: 'Resume Score Checks',
    job_matches: 'Job Matches',
    cover_letters: 'Cover Letter Generation',
    custom_templates: 'Custom Templates',
    pdf_export: 'PDF Export'
};

const UpgradeModal = ({ isOpen, onClose, feature, currentTier = 'basic', used = 0, limit = 0, creditBalance = 0 }) => {
    const [showCreditModal, setShowCreditModal] = useState(false);

    if (!isOpen) return null;

    let nextTier = NEXT_TIER[currentTier] || 'max';
    
    const nextLimit = stripeService.getFeatureLimit(nextTier, feature);
    const featureLabel = FEATURE_LABELS[feature] || feature;
    const user = authManager.getCurrentUser();
    const isCreditEligible = stripeService.isCreditEligible(feature);
    const creditCost = CREDIT_COSTS[feature] || 0;

    const handleUpgrade = async () => {
        if (!user?.email) return;
        try {
            await stripeService.createCheckoutSession(user.email, nextTier);
        } catch (err) {
            console.error('Upgrade error:', err);
            alert(err.message);
        }
    };

    const NextIcon = nextTier === 'max' ? Rocket : Crown;

    // If credit purchase modal is open, show that instead
    if (showCreditModal) {
        return (
            <CreditPurchaseModal
                isOpen={true}
                onClose={() => { setShowCreditModal(false); onClose(); }}
                feature={feature}
                creditBalance={creditBalance}
                onUpgrade={() => { setShowCreditModal(false); }}
            />
        );
    }

    return (
        <div className="upgrade-modal-overlay" onClick={onClose}>
            <div className="upgrade-modal glass-panel" onClick={e => e.stopPropagation()}>
                <div className="upgrade-modal-icon">
                    <Lock size={32} />
                </div>

                <h2 className="upgrade-modal-title">Limit Reached</h2>

                <p className="upgrade-modal-text">
                    {typeof stripeService.getFeatureLimit(currentTier, feature) === 'boolean' || limit === 0 ? (
                        <>
                            <strong>{featureLabel}</strong> is not available on the{' '}
                            <span className="tier-tag basic">{TIER_NAMES[currentTier]}</span> plan.
                        </>
                    ) : (
                        <>
                            You've used <strong>{used}/{limit}</strong> {featureLabel} on the{' '}
                            <span className="tier-tag basic">{TIER_NAMES[currentTier]}</span> plan.
                        </>
                    )}
                </p>

                <div className="upgrade-card">
                    <div className="upgrade-card-header">
                        <NextIcon size={20} />
                        <span>Upgrade to {TIER_NAMES[nextTier]}</span>
                    </div>
                    <div className="upgrade-card-body">
                        <span className="upgrade-feature-limit">
                            {typeof nextLimit === 'boolean' ? (nextLimit ? 'Full Access to' : 'No Access to') : (nextLimit === Infinity ? 'Unlimited' : `${nextLimit}x`)} {featureLabel}
                        </span>
                        <span className="upgrade-price">
                            {nextTier === 'pro' ? '$10.99' : '$29.99'}/mo
                        </span>
                    </div>
                </div>

                <div className="upgrade-modal-actions">
                    <button className={`upgrade-btn ${nextTier === 'max' ? 'max-tier-btn' : ''}`} onClick={handleUpgrade}>
                        Upgrade to {TIER_NAMES[nextTier]} <ArrowRight size={16} />
                    </button>

                    {isCreditEligible && (
                        <button className="buy-credits-btn" onClick={() => setShowCreditModal(true)}>
                            <Coins size={16} />
                            Buy Credits Instead
                            {creditCost > 0 && <span className="credit-cost-hint">({creditCost} credit{creditCost > 1 ? 's' : ''}/use)</span>}
                        </button>
                    )}

                    <button className="upgrade-dismiss" onClick={onClose}>
                        Maybe Later
                    </button>
                </div>
            </div>
        </div>
    );
};

export default UpgradeModal;
